import React from "react";
import {useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Card,
  CardMedia,
  Chip,
  Grid,
  Paper,
  Divider,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  Breadcrumbs,
  Link
} from "@mui/material";
import {
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  Phonelink as DeviceIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon
} from "@mui/icons-material";
import WhatsNewFormDialog from "../../components/WhatsNewFormDialog";
import { updateImage , fetchWhatsNewById} from "../../api/whatsNewApi";

export default function WhatsNew() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [openDialog, setOpenDialog] = useState(false);
  const [whatsNew, setWhatsNew] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // for Alet
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showAlert, setShowAlet] = useState(false);

  const getById = async () => {
    setLoading(true);
    try {
      const data = await fetchWhatsNewById(id);
      if (data.status) {
        setWhatsNew(data.data);
      }
    } catch (error) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getById();
  }, [id]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSuccess = (mes) => {
    setOpenDialog(false);
    setAlertMessage(mes);
    setShowAlet(true);
    getById();
  };

  const handleImageChange = async (e, id, type) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const result = await updateImage(file, id, type);
        if (result.success) {
          setAlertMessage("Image updated successfully");
          setAlertSeverity("success");
          setShowAlet(true);
          getById();
        } else {
          setAlertSeverity("error");
          setAlertMessage(result.error);
          setShowAlet(true);
        }
      } catch (error) {
        setAlertSeverity("error");
        setAlertMessage(error.toString());
        setShowAlet(true);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !whatsNew) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">
          {error || "Content not found"}
        </Typography>
      </Box>
    );
  }

  const statusColor = whatsNew.active ? "success" : "default";

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 4 } }}>
      {/* Navigation */}
      <Breadcrumbs 
        separator={<ChevronRightIcon fontSize="small" />} 
        sx={{ mb: 3 }}
      >
        <Link underline="hover" color="inherit" href="/adminDashboard" sx={{ display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </Link>
        <Link underline="hover" color="inherit" href="/view-all-whats-new">
          What's New
        </Link>
        <Typography color="text.primary">Detail</Typography>
      </Breadcrumbs>

      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          borderRadius: 1.5, 
          bgcolor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          mb: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          right: 0, 
          width: '30%', 
          height: '100%', 
          background: `linear-gradient(45deg, transparent, ${alpha(theme.palette.primary.main, 0.05)})`,
          zIndex: 0
        }} />
        
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip 
                label={whatsNew.active ? "Active" : "Archived"} 
                color={statusColor}
                size="small" 
                sx={{ fontWeight: 700, borderRadius: 1.5 }}
              />
              <Chip 
                label={whatsNew.type.toUpperCase()} 
                variant="outlined"
                size="small" 
                color="primary"
                sx={{ fontWeight: 700, borderRadius: 1.5 }}
              />
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 900, color: 'text.primary', mb: 1 }}>
              {whatsNew.title}
            </Typography>
            <Stack direction="row" spacing={2} color="text.secondary">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon fontSize="inherit" />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {new Date(whatsNew.featureDate).toLocaleDateString(undefined, { dateStyle: 'long' })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DeviceIcon fontSize="inherit" />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {whatsNew.type === 'both' ? 'Web & Mobile' : whatsNew.type === 'web' ? 'Web Only' : 'Mobile Only'}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{ px: 3, borderRadius: 2, height: 48 }}
          >
            Update Details
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={4}>
        {/* Description & metadata */}
        <Grid item xs={12} md={7}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            Description
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 4 }}>
            {whatsNew.description}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>Route Configuration</Typography>
          <Grid container spacing={2}>
            {(whatsNew.type === 'mob' || whatsNew.type === 'both') && (
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.secondary.main, 0.02) }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>MOBILE ROUTE</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, fontFamily: 'monospace' }}>
                    {whatsNew.mobRout || '—'}
                  </Typography>
                </Paper>
              </Grid>
            )}
            {(whatsNew.type === 'web' || whatsNew.type === 'both') && (
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>WEB ROUTE</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, fontFamily: 'monospace' }}>
                    {whatsNew.webRout || '—'}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Visuals */}
        <Grid item xs={12} md={5}>
          <Typography variant="h6" sx={{ mb: 2 }}>Gallery</Typography>
          <Stack spacing={3}>
            {/* Web Image */}
            {(whatsNew.type === "both" || whatsNew.type === "web") && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 800, mb: 1, display: 'block', color: 'text.secondary' }}>WEB PREVIEW</Typography>
                <Card sx={{ position: "relative", borderRadius: 1.5, bgcolor: '#000' }}>
                  <CardMedia
                    component="img"
                    image={whatsNew.webImageURL}
                    alt="Web Screenshot"
                    sx={{ width: '100%', height: 'auto', maxHeight: 300, opacity: 0.9 }}
                  />
                  <Box sx={{ position: "absolute", top: 12, right: 12 }}>
                    <Tooltip title="Replace Web Image">
                      <IconButton
                        component="label"
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.9)', 
                          color: 'primary.main',
                          '&:hover': { bgcolor: '#fff' }
                        }}
                      >
                        <EditIcon fontSize="small" />
                        <input type="file" hidden onChange={(e) => handleImageChange(e, whatsNew._id, "web")} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Card>
              </Box>
            )}

            {/* Mobile Image */}
            {(whatsNew.type === "both" || whatsNew.type === "mob") && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 800, mb: 1, display: 'block', color: 'text.secondary' }}>MOBILE PREVIEW</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Card sx={{ 
                    position: "relative", 
                    borderRadius: 1.5, 
                    width: 200, 
                    height: 400, 
                    border: '8px solid', 
                    borderColor: '#1e293b',
                    bgcolor: '#1e293b'
                  }}>
                    <CardMedia
                      component="img"
                      image={whatsNew.mobImageURL}
                      alt="Mobile Screenshot"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                    />
                    <Box sx={{ position: "absolute", top: 12, right: 12 }}>
                      <Tooltip title="Replace Mobile Image">
                        <IconButton
                          component="label"
                          sx={{ 
                            bgcolor: 'rgba(255,255,255,0.9)', 
                            color: 'primary.main',
                            '&:hover': { bgcolor: '#fff' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                          <input type="file" hidden onChange={(e) => handleImageChange(e, whatsNew._id, 'mob')} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Card>
                </Box>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <WhatsNewFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        createOrUpdate={"update"}
        entry={whatsNew}
        onSuccess={handleSuccess}
      />
    </Box>
  );
}

